import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { generateClient } from 'aws-amplify/api';
import { 
  listTasksByUser, 
  listTasksByStatus, 
  getTaskWithDetails,
  listOverdueTasks,
  listTasksDueToday 
} from '../../graphql/taskQueries';
import { 
  createEnhancedTask, 
  updateTaskStatus as updateTaskStatusMutation,
  startTimeTracking,
  stopTimeTracking
} from '../../graphql/taskMutations';

const client = generateClient();

// Enhanced async thunks for real GraphQL operations
export const fetchTasksByUser = createAsyncThunk(
  'tasks/fetchTasksByUser',
  async (userId, { rejectWithValue }) => {
    try {
      const result = await client.graphql({
        query: listTasksByUser,
        variables: { assignedUserId: userId, limit: 100 }
      });
      return result.data.listTasks.items;
    } catch (error) {
      return rejectWithValue('Failed to fetch user tasks');
    }
  }
);

export const fetchTasksByStatus = createAsyncThunk(
  'tasks/fetchTasksByStatus',
  async (status, { rejectWithValue }) => {
    try {
      const result = await client.graphql({
        query: listTasksByStatus,
        variables: { status, limit: 100 }
      });
      return { status, tasks: result.data.listTasks.items };
    } catch (error) {
      return rejectWithValue('Failed to fetch tasks by status');
    }
  }
);

export const fetchTaskDetails = createAsyncThunk(
  'tasks/fetchTaskDetails',
  async (taskId, { rejectWithValue }) => {
    try {
      const result = await client.graphql({
        query: getTaskWithDetails,
        variables: { id: taskId }
      });
      return result.data.getTask;
    } catch (error) {
      return rejectWithValue('Failed to fetch task details');
    }
  }
);

export const createTask = createAsyncThunk(
  'tasks/createTask',
  async (taskData, { rejectWithValue }) => {
    try {
      const result = await client.graphql({
        query: createEnhancedTask,
        variables: { input: taskData }
      });
      return result.data.createTask;
    } catch (error) {
      return rejectWithValue('Failed to create task');
    }
  }
);

export const updateTaskStatus = createAsyncThunk(
  'tasks/updateTaskStatus',
  async ({ taskId, status, completedAt }, { rejectWithValue }) => {
    try {
      const result = await client.graphql({
        query: updateTaskStatusMutation,
        variables: { 
          input: { 
            id: taskId, 
            status,
            completedAt: completedAt || (status === 'COMPLETED' ? new Date().toISOString() : null)
          } 
        }
      });
      return result.data.updateTask;
    } catch (error) {
      return rejectWithValue('Failed to update task status');
    }
  }
);

export const fetchOverdueTasks = createAsyncThunk(
  'tasks/fetchOverdueTasks',
  async (_, { rejectWithValue }) => {
    try {
      const today = new Date().toISOString().split('T')[0];
      const result = await client.graphql({
        query: listOverdueTasks,
        variables: { today, limit: 100 }
      });
      return result.data.listTasks.items;
    } catch (error) {
      return rejectWithValue('Failed to fetch overdue tasks');
    }
  }
);

export const fetchTasksDueToday = createAsyncThunk(
  'tasks/fetchTasksDueToday',
  async (_, { rejectWithValue }) => {
    try {
      const today = new Date().toISOString().split('T')[0];
      const result = await client.graphql({
        query: listTasksDueToday,
        variables: { today, limit: 100 }
      });
      return result.data.listTasks.items;
    } catch (error) {
      return rejectWithValue('Failed to fetch tasks due today');
    }
  }
);

export const startTaskTimer = createAsyncThunk(
  'tasks/startTaskTimer',
  async ({ taskId, userId }, { rejectWithValue }) => {
    try {
      const result = await client.graphql({
        query: startTimeTracking,
        variables: {
          taskId,
          userId,
          startTime: new Date().toISOString()
        }
      });
      return result.data.createTimeLog;
    } catch (error) {
      return rejectWithValue('Failed to start task timer');
    }
  }
);

export const stopTaskTimer = createAsyncThunk(
  'tasks/stopTaskTimer',
  async ({ timeLogId, duration, description }, { rejectWithValue }) => {
    try {
      const result = await client.graphql({
        query: stopTimeTracking,
        variables: {
          id: timeLogId,
          endTime: new Date().toISOString(),
          duration,
          description
        }
      });
      return result.data.updateTimeLog;
    } catch (error) {
      return rejectWithValue('Failed to stop task timer');
    }
  }
);

const initialState = {
  // Task collections
  allTasks: [],
  tasksByStatus: {
    PENDING: [],
    IN_PROGRESS: [],
    COMPLETED: [],
    ON_HOLD: [],
    APPROVED: [],
    REJECTED: []
  },
  userTasks: [],
  overdueTasks: [],
  tasksDueToday: [],
  
  // Selected task details
  selectedTask: null,
  
  // Time tracking
  activeTimeLog: null,
  
  // UI state
  loading: false,
  error: null,
  
  // Task Modal state
  taskModal: {
    isOpen: false,
    mode: 'view', // 'view', 'edit', 'create'
    taskData: null
  },
  
  // Filters and search
  filters: {
    status: null,
    priority: null,
    category: null,
    assignedUser: null,
    area: null
  },
  searchTerm: '',
  
  // Stats
  stats: {
    total: 0,
    completed: 0,
    overdue: 0,
    dueToday: 0,
    inProgress: 0
  }
};

const taskSlice = createSlice({
  name: 'tasks',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    setSearchTerm: (state, action) => {
      state.searchTerm = action.payload;
    },
    clearFilters: (state) => {
      state.filters = {
        status: null,
        priority: null,
        category: null,
        assignedUser: null,
        area: null
      };
      state.searchTerm = '';
    },
    setSelectedTask: (state, action) => {
      state.selectedTask = action.payload;
    },
    setActiveTimeLog: (state, action) => {
      state.activeTimeLog = action.payload;
    },
    addTaskToStatus: (state, action) => {
      const { task, status } = action.payload;
      if (state.tasksByStatus[status]) {
        state.tasksByStatus[status].push(task);
      }
    },
    removeTaskFromStatus: (state, action) => {
      const { taskId, status } = action.payload;
      if (state.tasksByStatus[status]) {
        state.tasksByStatus[status] = state.tasksByStatus[status].filter(
          task => task.id !== taskId
        );
      }
    },
    moveTaskBetweenStatuses: (state, action) => {
      const { taskId, fromStatus, toStatus } = action.payload;
      
      // Remove from old status
      if (state.tasksByStatus[fromStatus]) {
        const taskIndex = state.tasksByStatus[fromStatus].findIndex(t => t.id === taskId);
        if (taskIndex !== -1) {
          const [task] = state.tasksByStatus[fromStatus].splice(taskIndex, 1);
          task.status = toStatus;
          
          // Add to new status
          if (state.tasksByStatus[toStatus]) {
            state.tasksByStatus[toStatus].push(task);
          }
        }
      }
    },
    updateTaskInState: (state, action) => {
      const updatedTask = action.payload;
      
      // Update in all relevant arrays
      const updateTaskInArray = (array) => {
        const index = array.findIndex(t => t.id === updatedTask.id);
        if (index !== -1) {
          array[index] = { ...array[index], ...updatedTask };
        }
      };
      
      updateTaskInArray(state.allTasks);
      updateTaskInArray(state.userTasks);
      Object.values(state.tasksByStatus).forEach(updateTaskInArray);
      
      // Update selected task if it's the same
      if (state.selectedTask?.id === updatedTask.id) {
        state.selectedTask = { ...state.selectedTask, ...updatedTask };
      }
    },
    calculateStats: (state) => {
      const allTasks = Object.values(state.tasksByStatus).flat();
      state.stats = {
        total: allTasks.length,
        completed: state.tasksByStatus.COMPLETED.length,
        overdue: state.overdueTasks.length,
        dueToday: state.tasksDueToday.length,
        inProgress: state.tasksByStatus.IN_PROGRESS.length
      };
    },
    
    // Task Modal actions
    openTaskModal: (state, action) => {
      const { mode, taskData } = action.payload;
      state.taskModal = {
        isOpen: true,
        mode: mode || 'view',
        taskData: taskData || null
      };
    },
    closeTaskModal: (state) => {
      state.taskModal = {
        isOpen: false,
        mode: 'view',
        taskData: null
      };
    },
    setTaskModalMode: (state, action) => {
      state.taskModal.mode = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch tasks by user
      .addCase(fetchTasksByUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTasksByUser.fulfilled, (state, action) => {
        state.loading = false;
        state.userTasks = action.payload;
        state.error = null;
      })
      .addCase(fetchTasksByUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Fetch tasks by status
      .addCase(fetchTasksByStatus.fulfilled, (state, action) => {
        const { status, tasks } = action.payload;
        state.tasksByStatus[status] = tasks;
      })
      
      // Fetch task details
      .addCase(fetchTaskDetails.fulfilled, (state, action) => {
        state.selectedTask = action.payload;
      })
      
      // Create task
      .addCase(createTask.pending, (state) => {
        state.loading = true;
      })
      .addCase(createTask.fulfilled, (state, action) => {
        state.loading = false;
        const newTask = action.payload;
        state.allTasks.push(newTask);
        
        // Add to appropriate status array
        if (state.tasksByStatus[newTask.status]) {
          state.tasksByStatus[newTask.status].push(newTask);
        }
      })
      .addCase(createTask.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Update task status
      .addCase(updateTaskStatus.fulfilled, (state, action) => {
        const updatedTask = action.payload;
        
        // Update task in all relevant places
        taskSlice.caseReducers.updateTaskInState(state, { payload: updatedTask });
      })
      
      // Fetch overdue tasks
      .addCase(fetchOverdueTasks.fulfilled, (state, action) => {
        state.overdueTasks = action.payload;
      })
      
      // Fetch tasks due today
      .addCase(fetchTasksDueToday.fulfilled, (state, action) => {
        state.tasksDueToday = action.payload;
      })
      
      // Start task timer
      .addCase(startTaskTimer.fulfilled, (state, action) => {
        state.activeTimeLog = action.payload;
      })
      
      // Stop task timer
      .addCase(stopTaskTimer.fulfilled, (state, action) => {
        state.activeTimeLog = null;
      });
  },
});

export const { 
  clearError,
  setFilters,
  setSearchTerm,
  clearFilters,
  setSelectedTask,
  setActiveTimeLog,
  addTaskToStatus,
  removeTaskFromStatus,
  moveTaskBetweenStatuses,
  updateTaskInState,
  calculateStats,
  openTaskModal,
  closeTaskModal,
  setTaskModalMode
} = taskSlice.actions;

export default taskSlice.reducer;